const MAX_SAMPLE_ITEMS = 3;
const MAX_PATHS = 200;

/**
 * Flattens a webhook sample response's items into candidate dot-paths for the
 * field matcher's source-field picker. Arrays of objects flatten using only
 * their first element's keys, with NO index segment (`items.sku`, never
 * `items.0.sku`) — the backend's getAtPath is a naive `.split('.').reduce()`
 * with no array-index support, so suggested paths must stay within what it
 * can actually resolve.
 */
export function flattenSampleFields(sample: any[] | undefined | null): string[] {
  if (!Array.isArray(sample) || sample.length === 0) return [];

  const paths = new Set<string>();
  const items = sample.slice(0, MAX_SAMPLE_ITEMS);

  for (const item of items) {
    flattenInto(item, '', paths);
    if (paths.size >= MAX_PATHS) break;
  }

  return Array.from(paths).sort().slice(0, MAX_PATHS);
}

function flattenInto(value: any, prefix: string, paths: Set<string>): void {
  if (paths.size >= MAX_PATHS) return;

  if (value === null || value === undefined) {
    if (prefix) paths.add(prefix);
    return;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      if (prefix) paths.add(prefix);
      return;
    }
    const first = value[0];
    if (first !== null && typeof first === 'object' && !Array.isArray(first)) {
      flattenInto(first, prefix, paths);
    } else if (prefix) {
      paths.add(prefix);
    }
    return;
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      if (prefix) paths.add(prefix);
      return;
    }
    for (const key of keys) {
      flattenInto(value[key], prefix ? `${prefix}.${key}` : key, paths);
      if (paths.size >= MAX_PATHS) return;
    }
    return;
  }

  if (prefix) paths.add(prefix);
}
